"use client";

// BosAI support widget — Stage 1 shell. Replaces the tawk.to widget with an
// FB-native, "BosAI"-branded chat. A floating launcher opens a bottom-right chat
// panel that streams replies from /api/bosai/chat over Server-Sent Events (the
// same SSE shape the Kutra help chat consumes).
//
// Stage 1 is the SHELL + pipe: the API returns a deterministic placeholder, so the
// UI is real but there is no model behind it yet. STAGE 2 swaps in Claude Haiku +
// security gates (server-side); STAGE 3 wires real specialist escalation.
//
// Refund + case (contact) flows run ENTIRELY INSIDE the chat panel — no redirect,
// no new tab. Identity is decided server-side (mirroring /api/bosai/escalate): the
// widget never reads the auth session itself. Refund is login-gated — the payments
// endpoint answers 401/login_required for anonymous visitors and the chat then
// offers a sign-in link. Case/contact is open to anyone: anonymous visitors are
// asked for a notification email in-chat (the email_required pattern).
//
// Visibility mirrors the tawk widget it replaces: shown on public/marketing pages,
// hidden on /dashboard. No external requests; nothing secret ever reaches the client.

import * as React from "react";
import { Sparkles, Send, X, LifeBuoy, FileText, ShieldCheck, BadgeCheck, Receipt, Phone, Mail, CreditCard, LogIn, RotateCcw, Package, CalendarClock, Building2, Compass } from "lucide-react";
import { BOSAI_OPEN_EVENT, contextNotice, markBosAiReady, type BosAiOpenDetail } from "@/lib/bosai-open";
import { askBosAiContext, type AskBosAiContext } from "@/app/lib/user-admin-shell/bosai-context";
import { CASE_REQUEST_TIMEOUT_MS, readCaseOutcome } from "@/lib/bosai-case-result";
import { armChatCeiling } from "@/lib/bosai-chat-timeout";
import { nextAuthIdentity } from "@/lib/bosai-auth-reset";
import { useAuth } from "@/contexts/auth-context";
import type { OrderStatusCard } from "@/lib/bosai-order-status";
import type { DeadlinesCard } from "@/lib/bosai-deadlines";
import type { BusinessInfoCard } from "@/lib/bosai-business-info";
import type { ServiceCta, ServiceRecommendationCard } from "@/lib/bosai-service-recommendation";

// Reuse the app's BosAI brand gradient (see app/ui/dashboard/SearchLinks.tsx
// "Ask BosAI"). Those tokens (--blue-400 / --royal-500) are scoped to `.fb-admin`,
// so on public pages we use their concrete hex values to keep the exact look.
const BOSAI_GRADIENT = "linear-gradient(150deg, #2FABE1, #3A50B5)";
const BOSAI_GLOW = "0 3px 9px -3px rgba(0,141,210,0.5)";
// Secondary (support) gradient for the non-sales chips (refund, callback, contact)
// so they read as a distinct group from the sales-first chips above them.
const SUPPORT_GRADIENT = "linear-gradient(150deg,#64748B,#475569)";

const ENDPOINT = "/api/bosai/chat";
const SESSION_KEY = "bosai-session-id";

const GREETING =
  "Hi, I'm BosAI, File.Business's AI assistant. Ask me anything about your business filings, or pick one below.";

// First-open quick actions. Sales-first (the filings we actively help start), then
// a divider, then the support group: refund (login-gated) and the contact/case
// chips. Sales chips send a plain-English opener into the chat; refund and case
// chips start their own in-chat flows. Copy is English on purpose — customer-facing.
type QuickAction = {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  kind: "sales" | "refund" | "case";
  prompt?: string; // sales chips only
  caseType?: string; // case chips only
};
const QUICK_ACTIONS: QuickAction[] = [
  { label: "File an Annual Report", prompt: "I'd like to file my Annual Report.", Icon: FileText, kind: "sales" },
  { label: "Registered Agent service", prompt: "I'm interested in your Registered Agent service.", Icon: ShieldCheck, kind: "sales" },
  { label: "Certificate of Status", prompt: "I need a Certificate of Status.", Icon: BadgeCheck, kind: "sales" },
  { label: "Request a refund or dispute", Icon: Receipt, kind: "refund" },
  { label: "Request a callback", Icon: Phone, kind: "case", caseType: "callback" },
  { label: "Contact request", Icon: Mail, kind: "case", caseType: "contact_request" },
];

// A payment as returned by GET /api/bosai/refund/payments. The bk2 contract is
// still being built, so we read defensively: prefer a server-formatted amount/label
// when present and fall back to formatting the raw fields ourselves. `id` is the
// only field we require (it is what we POST back to create the refund).
type Payment = {
  transactionId: string; // server (getMyRecentPayments) id field
  serviceId?: string | null; // service the payment is attached to; REQUIRED to open a refund (null -> not selectable)
  description?: string | null;
  label?: string;
  amount?: number; // major units (dollars), unless amountFormatted is provided
  amountFormatted?: string;
  currency?: string;
  createdAt?: string | null;
};

// In-chat multi-turn flows. When a flow is active the composer's next line feeds
// that flow instead of the chat model (mirrors the old awaitingEmail pattern,
// generalised). "none" = normal chat.
type Flow =
  | { kind: "none" }
  | { kind: "refundReason"; payment: Payment }
  | { kind: "caseMessage"; caseType: string; label: string }
  | { kind: "caseEmail"; caseType: string; label: string; message: string }
  | { kind: "escalateEmail" };

type Role = "user" | "assistant" | "system";
type Msg = {
  role: Role;
  content: string;
  streaming?: boolean;
  filler?: boolean;
  // Page-context notice from an in-app open; does not count as visitor activity.
  notice?: boolean;
  // Rich, interactive message variants rendered inside the transcript.
  // "retry": an honest failure line with a Try again button (case/escalate).
  ui?: "login" | "payments" | "retry";
  payments?: Payment[];
  // H8: structured "Order status" card sent by the server as its own SSE event
  // (rendered from DB rows, never parsed out of the model reply).
  orderCard?: OrderStatusCard;
  // H8: structured "Upcoming deadlines" card, same delivery as orderCard.
  deadlinesCard?: DeadlinesCard;
  // H8: structured "Business info" card, same delivery as orderCard.
  businessCard?: BusinessInfoCard;
  // H8: structured "Recommended service" card, same delivery as orderCard.
  serviceCard?: ServiceRecommendationCard;
};

const EMAIL_PLACEHOLDER = "Enter your email…";

function formatMoney(amount?: number, currency = "usd"): string {
  if (amount == null || Number.isNaN(amount)) return "";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatDate(s?: string): string {
  if (!s) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// A YYYY-MM-DD day from the server, shown as that same calendar day in any timezone.
function formatDay(s: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const d = new Date(`${s}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

const CHAT_ERROR_TEXT = "Sorry, something went wrong. Please try again, or talk to an expert.";
const CHAT_NETWORK_TEXT = "Sorry, I couldn't reach support just now. Please try again.";

// A failed chat turn loses its in-flight assistant bubble (empty, filler or a
// truncated reply); the user turn stays, so Try again re-sends it, not re-adds it.
export function dropStreamingReply<M extends { streaming?: boolean }>(msgs: M[]): M[] {
  const idx = msgs.map((m) => Boolean(m.streaming)).lastIndexOf(true);
  return idx < 0 ? msgs : msgs.filter((_, i) => i !== idx);
}

// A "done" settles the in-flight bubble only when a real answer (token or card)
// arrived; a done after filler / nothing leaves it streaming, so the failure path
// can still find and drop it before showing Try again.
export function settleOnDone<M extends { streaming?: boolean }>(msgs: M[], answered: boolean): M[] {
  if (!answered) return msgs;
  const idx = msgs.map((m) => Boolean(m.streaming)).lastIndexOf(true);
  return idx < 0 ? msgs : msgs.map((m, i) => (i === idx ? { ...m, streaming: false } : m));
}

// A stream that ends without an explicit "done" but with real tokens is kept as
// the answer; a filler-only or empty one never gets here (runChat turns it into
// a failure line + Try again, and dropStreamingReply removes the filler bubble).
export function settleUnfinishedReply<M extends { filler?: boolean; streaming?: boolean }>(m: M): M {
  return { ...m, filler: false, streaming: false };
}

// Escalating wait text (H6 #3), same copy and thresholds as the static widget.
export const BOSAI_WAIT_STEPS: ReadonlyArray<readonly [number, string]> = [
  [20000, "Still working on it, complex questions can take up to a minute…"],
  [60000, "Taking longer than usual. You can keep waiting or talk to an expert."],
];

// Shows each wait step via `show` unless the turn is already answered. stop()
// must run on the first token / card, an error, and when the turn settles.
export function armWaitText(show: (text: string) => void, answered: () => boolean) {
  let timers = BOSAI_WAIT_STEPS.map(([ms, text]) =>
    setTimeout(() => {
      if (!answered()) show(text);
    }, ms),
  );
  return {
    stop: () => {
      timers.forEach(clearTimeout);
      timers = [];
    },
  };
}

// The wait text rides in the in-flight bubble as filler: the first token
// replaces it, a card retires it, and a turn that ends on it fails (the bubble
// is dropped), so it never stands as the answer or reaches the outbound thread.
export function applyWaitText<M extends { content: string; filler?: boolean }>(m: M, text: string): M {
  return { ...m, content: text, filler: true };
}

// Defensive read of the server's card event: anything off-shape is dropped.
export function readOrderCard(v: unknown): OrderStatusCard | null {
  const c = v as Partial<OrderStatusCard> | null;
  if (!c || c.kind !== "order_status" || !Array.isArray(c.orders)) return null;
  const str = (x: unknown) => (typeof x === "string" ? x : "");
  return {
    kind: "order_status",
    total: typeof c.total === "number" ? c.total : c.orders.length,
    orders: c.orders.slice(0, 10).map((o) => ({
      orderNo: str(o?.orderNo),
      service: str(o?.service),
      state: str(o?.state),
      status: str(o?.status),
      lastUpdate: str(o?.lastUpdate),
    })),
  };
}

export function readDeadlinesCard(v: unknown): DeadlinesCard | null {
  const c = v as Partial<DeadlinesCard> | null;
  if (!c || c.kind !== "deadlines" || !Array.isArray(c.deadlines)) return null;
  const str = (x: unknown) => (typeof x === "string" ? x : "");
  return {
    kind: "deadlines",
    total: typeof c.total === "number" ? c.total : c.deadlines.length,
    deadlines: c.deadlines.slice(0, 10).map((d) => ({
      company: str(d?.company),
      filing: str(d?.filing),
      state: str(d?.state),
      due: str(d?.due),
      daysLeft: typeof d?.daysLeft === "number" && Number.isFinite(d.daysLeft) ? Math.trunc(d.daysLeft) : 0,
    })),
  };
}

function daysLeftLabel(n: number): string {
  if (n < 0) return `${-n} day${n === -1 ? "" : "s"} overdue`;
  if (n === 0) return "Due today";
  return `${n} day${n === 1 ? "" : "s"} left`;
}

export function DeadlinesCardView({ card }: { card: DeadlinesCard }) {
  return (
    <div className="w-full max-w-[88%] rounded-xl border border-border bg-background px-3 py-2.5" data-testid="bosai-deadlines-card">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: BOSAI_GRADIENT }}>
          <CalendarClock className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-foreground">Upcoming deadlines</span>
      </div>
      {card.deadlines.length === 0 ? (
        <p className="text-xs text-muted-foreground">No upcoming deadlines on your account in the next 60 days.</p>
      ) : (
        <ul className="space-y-1.5">
          {card.deadlines.map((d, i) => (
            <li key={`${d.company}-${d.filing}-${i}`} className="rounded-lg bg-muted/50 px-2.5 py-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-foreground">{d.filing}{d.state ? ` · ${d.state}` : ""}</span>
                <span
                  className={
                    d.daysLeft < 0
                      ? "shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800 dark:bg-red-950/50 dark:text-red-300"
                      : "shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800 dark:bg-sky-950/50 dark:text-sky-300"
                  }
                >
                  {daysLeftLabel(d.daysLeft)}
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2 text-muted-foreground">
                <span className="truncate">{d.company}</span>
                {d.due ? <span className="shrink-0">Due {formatDay(d.due)}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {card.total > card.deadlines.length ? (
        <p className="mt-1.5 text-[10px] text-muted-foreground">Showing the {card.deadlines.length} soonest of {card.total}.</p>
      ) : null}
    </div>
  );
}

export function readBusinessCard(v: unknown): BusinessInfoCard | null {
  const c = v as Partial<BusinessInfoCard> | null;
  if (!c || c.kind !== "business_info" || !Array.isArray(c.companies)) return null;
  const str = (x: unknown) => (typeof x === "string" ? x : "");
  return {
    kind: "business_info",
    total: typeof c.total === "number" ? c.total : c.companies.length,
    companies: c.companies.slice(0, 10).map((b) => ({
      name: str(b?.name),
      state: str(b?.state),
      entityType: str(b?.entityType),
      status: str(b?.status),
      registeredAgent: str(b?.registeredAgent),
      address: str(b?.address),
      addressType: b?.addressType === "principal" || b?.addressType === "mailing" ? b.addressType : "",
    })),
  };
}

export function BusinessInfoCardView({ card }: { card: BusinessInfoCard }) {
  return (
    <div className="w-full max-w-[88%] rounded-xl border border-border bg-background px-3 py-2.5" data-testid="bosai-business-card">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: BOSAI_GRADIENT }}>
          <Building2 className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-foreground">Business info</span>
      </div>
      {card.companies.length === 0 ? (
        <p className="text-xs text-muted-foreground">We couldn&apos;t find any companies on your account.</p>
      ) : (
        <ul className="space-y-1.5">
          {card.companies.map((b, i) => (
            <li key={`${b.name}-${i}`} className="rounded-lg bg-muted/50 px-2.5 py-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-foreground">{b.name}</span>
                <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800 dark:bg-sky-950/50 dark:text-sky-300">
                  {b.status}
                </span>
              </div>
              <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-muted-foreground">
                <dt>Type</dt>
                <dd className="truncate text-foreground">{[b.entityType, b.state].filter(Boolean).join(" · ") || "Not on file"}</dd>
                <dt>Registered agent</dt>
                <dd className="truncate text-foreground">{b.registeredAgent || "Not on file"}</dd>
                <dt>{b.addressType === "mailing" ? "Mailing address" : "Address"}</dt>
                <dd className="text-foreground">{b.address || "Not on file"}</dd>
              </dl>
            </li>
          ))}
        </ul>
      )}
      {card.total > card.companies.length ? (
        <p className="mt-1.5 text-[10px] text-muted-foreground">Showing {card.companies.length} of {card.total} companies.</p>
      ) : null}
    </div>
  );
}

// Only same-site relative links survive ("/forms/..", "/file-*.html",
// "/contact.html?..#sales"); anything else drops the row.
const SAFE_HREF = /^\/(?![/\\])[A-Za-z0-9\-._~/?=&#%]*$/;
const CTA_LABEL: Record<ServiceCta, string> = { start: "Start filing", learn: "Learn more", expert: "Talk to an expert" };

export function readServiceCard(v: unknown): ServiceRecommendationCard | null {
  const c = v as Partial<ServiceRecommendationCard> | null;
  if (!c || c.kind !== "service_recommendation" || !Array.isArray(c.services)) return null;
  const str = (x: unknown) => (typeof x === "string" ? x : "");
  const services = c.services
    .slice(0, 3)
    .map((s) => ({
      slug: str(s?.slug),
      title: str(s?.title),
      summary: str(s?.summary),
      href: str(s?.href),
      cta: (s?.cta === "start" || s?.cta === "expert" ? s.cta : "learn") as ServiceCta,
    }))
    .filter((s) => s.title && SAFE_HREF.test(s.href));
  if (services.length === 0) return null;
  const state = str(c.state);
  return { kind: "service_recommendation", state: /^[A-Z]{2}$/.test(state) ? state : "", services };
}

export function ServiceRecommendationCardView({ card }: { card: ServiceRecommendationCard }) {
  return (
    <div className="w-full max-w-[88%] rounded-xl border border-border bg-background px-3 py-2.5" data-testid="bosai-service-card">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: BOSAI_GRADIENT }}>
          <Compass className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-foreground">{card.services.length > 1 ? "Recommended services" : "Recommended service"}</span>
      </div>
      <ul className="space-y-1.5">
        {card.services.map((s, i) => (
          <li key={`${s.slug}-${i}`} className="rounded-lg bg-muted/50 px-2.5 py-2 text-xs">
            <div className="font-medium text-foreground">
              {s.title}
              {card.state ? ` · ${card.state}` : ""}
            </div>
            <p className="mt-0.5 text-muted-foreground">{s.summary}</p>
            <a href={s.href} className="mt-1 inline-block font-semibold text-sky-700 hover:underline dark:text-sky-300">
              {CTA_LABEL[s.cta]} →
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OrderStatusCardView({ card }: { card: OrderStatusCard }) {
  return (
    <div className="w-full max-w-[88%] rounded-xl border border-border bg-background px-3 py-2.5" data-testid="bosai-order-card">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: BOSAI_GRADIENT }}>
          <Package className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-foreground">Order status</span>
      </div>
      {card.orders.length === 0 ? (
        <p className="text-xs text-muted-foreground">We couldn&apos;t find any orders on your account.</p>
      ) : (
        <ul className="space-y-1.5">
          {card.orders.map((o, i) => (
            <li key={`${o.orderNo}-${i}`} className="rounded-lg bg-muted/50 px-2.5 py-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-foreground">{o.service}{o.state ? ` · ${o.state}` : ""}</span>
                <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800 dark:bg-sky-950/50 dark:text-sky-300">
                  {o.status}
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2 text-muted-foreground">
                <span className="truncate">Order {o.orderNo}</span>
                {o.lastUpdate ? <span className="shrink-0">Updated {formatDay(o.lastUpdate)}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {card.total > card.orders.length ? (
        <p className="mt-1.5 text-[10px] text-muted-foreground">Showing {card.orders.length} most recent of {card.total}.</p>
      ) : null}
    </div>
  );
}

function paymentTitle(p: Payment): string {
  return p.description || p.label || "Payment";
}
function paymentMeta(p: Payment): string {
  const money = p.amountFormatted || formatMoney(p.amount, p.currency);
  const date = formatDate(p.createdAt ?? undefined);
  return [money, date].filter(Boolean).join(" · ");
}

// The page sent with each chat turn. Inside the User Admin shell it is reduced to
// known nav keys (no record ids); the server re-validates it either way.
function outboundPath(): string {
  const path = window.location.pathname;
  return askBosAiContext(path)?.path ?? path;
}

// Stable per-session id: reused across opens within the tab, regenerated per tab.
// `rotate` issues a fresh id (auth identity change, T-gap-1226).
function useSessionId(): [string, () => void] {
  const [id, setId] = React.useState(() => {
    if (typeof window === "undefined") return "";
    try {
      const existing = window.sessionStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      const fresh = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_KEY, fresh);
      return fresh;
    } catch {
      return crypto.randomUUID();
    }
  });
  const rotate = React.useCallback(() => {
    const fresh = crypto.randomUUID();
    try {
      window.sessionStorage.setItem(SESSION_KEY, fresh);
    } catch {
      // storage unavailable: the in-memory id still rotates
    }
    setId(fresh);
  }, []);
  return [id, rotate];
}

export default function BosAiWidget() {
  const [sessionId, rotateSessionId] = useSessionId();
  const { user, loading: authLoading } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [msgs, setMsgs] = React.useState<Msg[]>([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [flow, setFlow] = React.useState<Flow>({ kind: "none" });

  const launcherRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const endRef = React.useRef<HTMLDivElement>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  // The last failed chat turn / case / escalate submission, re-run by "Try again".
  const retryRef = React.useRef<(() => void) | null>(null);

  // Focus the composer on open; return focus to the launcher on close.
  React.useEffect(() => {
    if (open) inputRef.current?.focus();
    else launcherRef.current?.focus();
  }, [open]);

  // Keep the newest message in view as it streams.
  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  // Other surfaces (e.g. the User Admin topbar) open the panel via openBosAi().
  // An open may carry page context (path + module/topic, no user data); it is
  // announced once per page together with the AI disclosure.
  const pageCtxRef = React.useRef<AskBosAiContext | null>(null);
  React.useEffect(() => {
    const onOpen = (e: Event) => {
      const ctx = (e as CustomEvent<BosAiOpenDetail | undefined>).detail?.context ?? null;
      if (ctx && ctx.path !== pageCtxRef.current?.path) {
        setMsgs((cur) => [...cur, { role: "system", content: contextNotice(ctx), notice: true }]);
      }
      pageCtxRef.current = ctx;
      setOpen(true);
    };
    window.addEventListener(BOSAI_OPEN_EVENT, onOpen);
    markBosAiReady(true);
    return () => {
      window.removeEventListener(BOSAI_OPEN_EVENT, onOpen);
      markBosAiReady(false);
    };
  }, []);

  // Stops the in-flight turn's wait-text timers (see armWaitText).
  const waitRef = React.useRef<{ stop: () => void } | null>(null);

  // Abort any in-flight stream if the widget unmounts (e.g. route → /dashboard).
  React.useEffect(
    () => () => {
      waitRef.current?.stop();
      abortRef.current?.abort();
    },
    [],
  );

  // The widget lives in the root layout, so its state survives soft sign-out and
  // sign-in. When the settled auth user id changes, drop the transcript, any
  // in-chat flow and the session id so the next account never sees or escalates
  // the previous account's chat (T-gap-1226).
  const authIdRef = React.useRef<string | null | undefined>(undefined);
  const userId = user?.id ?? null;
  React.useEffect(() => {
    const next = nextAuthIdentity(authIdRef.current, { loading: authLoading, userId });
    authIdRef.current = next.settled;
    if (!next.reset) return;
    waitRef.current?.stop();
    abortRef.current?.abort();
    retryRef.current = null;
    pageCtxRef.current = null;
    setMsgs([{ role: "assistant", content: GREETING }]);
    setFlow({ kind: "none" });
    setInput("");
    setBusy(false);
    rotateSessionId();
  }, [authLoading, userId, rotateSessionId]);

  const pushSystem = (content: string) => setMsgs((cur) => [...cur, { role: "system", content }]);
  const pushUser = (content: string) => setMsgs((cur) => [...cur, { role: "user", content }]);
  const pushRetry = (content: string, retry: () => void) => {
    retryRef.current = retry;
    setMsgs((cur) => [...cur, { role: "system", content, ui: "retry" }]);
  };
  const onRetry = () => {
    const fn = retryRef.current;
    if (!fn || busy) return;
    retryRef.current = null;
    fn();
  };

  // The chat so far (user/assistant turns only) — sent with a case so the server
  // can store a short redacted summary for staff. Bounded to the API's 60 turns.
  const chatTranscript = () =>
    msgs
      .filter((m) => (m.role === "user" || m.role === "assistant") && m.content.trim())
      .map((m) => ({ role: m.role, content: m.content.slice(0, 8000) }))
      .slice(-60);

  const patchStreaming = (fn: (m: Msg) => Msg) =>
    setMsgs((cur) => {
      const idx = [...cur].reverse().findIndex((m) => m.streaming);
      if (idx < 0) return cur;
      const real = cur.length - 1 - idx;
      return cur.map((m, i) => (i === real ? fn(m) : m));
    });

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;

    // While a guided flow is active, the next composer line feeds THAT flow, not
    // the chat model. A short "cancel" word backs the visitor out of any flow.
    if (flow.kind !== "none") {
      setInput("");
      if (/^(cancel|no|no thanks|nvm|never ?mind|stop)$/i.test(q)) {
        setFlow({ kind: "none" });
        pushSystem("No problem — ask me anything else.");
        return;
      }
      if (flow.kind === "escalateEmail") {
        void doEscalate(q);
        return;
      }
      if (flow.kind === "refundReason") {
        pushUser(q);
        void submitRefund(flow.payment, q);
        return;
      }
      if (flow.kind === "caseMessage") {
        pushUser(q);
        void submitCase(flow.caseType, flow.label, q, null);
        return;
      }
      if (flow.kind === "caseEmail") {
        // The email is not echoed as a user bubble (keep it out of the transcript UI).
        void submitCase(flow.caseType, flow.label, flow.message, q);
        return;
      }
      return;
    }

    setInput("");
    retryRef.current = null;

    // The running thread we send is user+assistant turns only (system lines are
    // UI-local). Append the new user turn; runChat adds the bubble to stream into.
    const priorForApi = msgs
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, content: m.content }));
    const outbound = [...priorForApi, { role: "user" as const, content: q }];

    setMsgs((cur) => [...cur, { role: "user", content: q }]);
    void runChat(outbound);
  };

  // Streams one assistant reply for `outbound` (whose last turn is the user's).
  // On failure the bubble is dropped and Try again re-runs this with the same
  // thread, so the user turn is never appended twice.
  const runChat = async (outbound: { role: Role; content: string }[]) => {
    setBusy(true);
    setMsgs((cur) => [...cur, { role: "assistant", content: "", streaming: true }]);

    let failed: string | null = null;
    let answered = false;
    const ac = new AbortController();
    abortRef.current = ac;
    // A turn that outlives the server's bridge timeout is aborted and settles as
    // the network error; an unmount abort stays silent.
    const ceiling = armChatCeiling(ac);
    const wait = armWaitText(
      (text) => patchStreaming((m) => applyWaitText(m, text)),
      () => answered,
    );
    waitRef.current = wait;

    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, messages: outbound, page: { path: outboundPath() } }),
        signal: ac.signal,
      });
      if (!res.ok || !res.body) throw new Error("no stream");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finished = false;

      while (!finished) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() ?? "";
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          let payload: { type: string; text?: string; card?: unknown };
          try {
            payload = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (payload.type === "card") {
            const card = readOrderCard(payload.card);
            const deadlines = card ? null : readDeadlinesCard(payload.card);
            const business = card || deadlines ? null : readBusinessCard(payload.card);
            const service = card || deadlines || business ? null : readServiceCard(payload.card);
            // A readable card is a real answer even with no text; it retires the filler.
            if (card || deadlines || business || service) answered = true;
            if (answered) wait.stop();
            const clearFiller = (m: Msg) => (m.filler ? { ...m, content: "", filler: false } : m);
            if (card) patchStreaming((m) => ({ ...clearFiller(m), orderCard: card }));
            else if (deadlines) patchStreaming((m) => ({ ...clearFiller(m), deadlinesCard: deadlines }));
            else if (business) patchStreaming((m) => ({ ...clearFiller(m), businessCard: business }));
            else if (service) patchStreaming((m) => ({ ...clearFiller(m), serviceCard: service }));
          } else if (payload.type === "filler" && payload.text && !answered) {
            // Transient "thinking" line shown while the opus5 turn runs; the
            // first real token replaces it.
            patchStreaming((m) => ({ ...m, content: payload.text as string, filler: true }));
          } else if (payload.type === "token" && payload.text) {
            answered = true;
            wait.stop();
            patchStreaming((m) => ({
              ...m,
              content: (m.filler ? "" : m.content) + payload.text,
              filler: false,
            }));
          } else if (payload.type === "done") {
            const settled = answered;
            setMsgs((cur) => settleOnDone(cur, settled));
            finished = true;
          } else if (payload.type === "error") {
            wait.stop();
            failed = CHAT_ERROR_TEXT;
            finished = true;
          }
        }
      }
      // Stream ended with no real answer (no token, no card: empty or only the filler) → failure.
      if (!failed && !answered) failed = CHAT_NETWORK_TEXT;
      // Stream ended without an explicit done → settle the real reply anyway
      // (no-op after done, which already settled the bubble).
      if (!failed) patchStreaming(settleUnfinishedReply);
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError" && !ceiling.timedOut()) return;
      failed = CHAT_NETWORK_TEXT;
    } finally {
      ceiling.clear();
      wait.stop();
      if (waitRef.current === wait) waitRef.current = null;
      setBusy(false);
      abortRef.current = null;
    }
    if (failed) {
      setMsgs(dropStreamingReply);
      pushRetry(failed, () => void runChat(outbound));
    }
  };

  // ---- Refund flow (login-gated, entirely in-chat) --------------------------
  // Fetch the visitor's last-60-days payments. Identity is server-side: an
  // anonymous caller gets 401 / { error: "login_required" } and we surface an
  // in-chat sign-in link instead of a redirect.
  const startRefund = async () => {
    if (busy || flow.kind !== "none") return;
    setBusy(true);
    try {
      const res = await fetch("/api/bosai/refund/payments", { headers: { Accept: "application/json" } });
      const d = (await res.json().catch(() => ({}))) as { payments?: Payment[]; error?: string };
      if (res.status === 401 || d.error === "login_required") {
        setMsgs((cur) => [
          ...cur,
          {
            role: "system",
            content:
              "Refunds are handled right here in chat — but for your protection you'll need to sign in to the account the payment was made from.",
            ui: "login",
          },
        ]);
        return;
      }
      if (!res.ok) throw new Error("payments failed");
      const payments = (d.payments ?? []).filter((p) => p && p.transactionId);
      if (!payments.length) {
        pushSystem(
          "I couldn't find any payments on your account from the last 60 days. If that seems wrong, ask to talk to an expert and we'll look into it.",
        );
        return;
      }
      setMsgs((cur) => [
        ...cur,
        {
          role: "assistant",
          content: "Here are your payments from the last 60 days. Pick the one you'd like to refund.",
          ui: "payments",
          payments,
        },
      ]);
    } catch {
      pushSystem("I couldn't load your payments just now — please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  // A payment card was tapped → ask for a reason, then create the refund.
  const chooseRefundPayment = (p: Payment) => {
    if (busy || flow.kind !== "none") return;
    setFlow({ kind: "refundReason", payment: p });
    pushUser(`Refund: ${paymentTitle(p)}`);
    pushSystem("Got it. In a sentence or two, what's the reason for the refund?");
  };

  const submitRefund = async (payment: Payment, reason: string) => {
    setBusy(true);
    try {
      const res = await fetch("/api/bosai/refund/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The route's `reason` is a fixed enum; a chat user types free text, so we
        // send it as `explanation` under the enum "Other" (matches route contract).
        body: JSON.stringify({ serviceId: payment.serviceId, paymentId: payment.transactionId, reason: "Other", explanation: reason }),
      });
      const d = (await res.json().catch(() => ({}))) as {
        error?: string;
        refundId?: string;
        ticketCode?: string;
      };
      if (res.status === 401 || d.error === "login_required") {
        setFlow({ kind: "none" });
        setMsgs((cur) => [
          ...cur,
          { role: "system", content: "Your session expired — please sign in again to submit this refund.", ui: "login" },
        ]);
        return;
      }
      if (!res.ok) throw new Error("refund failed");
      setFlow({ kind: "none" });
      const ref = d.refundId || d.ticketCode ? ` (ref ${d.refundId ?? d.ticketCode})` : "";
      pushSystem(
        `Done — I've submitted your refund request${ref}. You'll get an email once it's reviewed, and any approved refund returns to your original payment method.`,
      );
    } catch {
      setFlow({ kind: "none" });
      pushSystem("I couldn't submit that refund just now — please try again, or ask to talk to an expert.");
    } finally {
      setBusy(false);
    }
  };

  // ---- Case / contact flow (no login required, entirely in-chat) ------------
  const startCase = (caseType: string, label: string) => {
    if (busy || flow.kind !== "none") return;
    setFlow({ kind: "caseMessage", caseType, label });
    pushUser(label);
    pushSystem(`Sure — tell me briefly what you need and I'll pass it to a specialist.`);
  };

  const submitCase = async (caseType: string, label: string, message: string, email: string | null) => {
    setBusy(true);
    const retry = () => void submitCase(caseType, label, message, email);
    try {
      const res = await fetch("/api/bosai/case/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, caseType, message, transcript: chatTranscript() }),
        signal: AbortSignal.timeout(CASE_REQUEST_TIMEOUT_MS),
      });
      const out = readCaseOutcome(res.status, await res.json().catch(() => null));
      if (out.kind === "needEmail") {
        // Anonymous visitor (or a bad address): collect the notification email in-chat.
        setFlow({ kind: "caseEmail", caseType, label, message });
        pushSystem(
          out.invalid
            ? "That doesn't look like a valid email — could you double-check it?"
            : "What's the best email for us to reach you and send a confirmation?",
        );
        return;
      }
      setFlow({ kind: "none" });
      // Success copy ONLY on 2xx + a case id; otherwise an honest failure + retry.
      if (out.kind === "error") {
        pushRetry(
          out.rateLimited
            ? "Your request was NOT submitted — too many attempts. Please wait a few minutes, then try again."
            : "Your request was NOT submitted — something went wrong on our side.",
          retry,
        );
        return;
      }
      const ref = out.ref ? ` (case ${out.ref})` : "";
      pushSystem(
        `Thanks — your request has been received${ref}. We've sent the details to your email and a specialist will follow up shortly.`,
      );
    } catch {
      setFlow({ kind: "none" });
      pushRetry("Your request was NOT submitted — we couldn't reach the server.", retry);
    } finally {
      setBusy(false);
    }
  };

  // Open a real support case with the transcript + email the visitor. Anonymous
  // visitors are asked for an email first (server returns email_required).
  const doEscalate = async (providedEmail?: string) => {
    const transcript = chatTranscript();
    if (!transcript.length || busy) return;
    setBusy(true);
    const retry = () => void doEscalate(providedEmail);
    try {
      const res = await fetch("/api/bosai/escalate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, transcript, email: providedEmail }),
        signal: AbortSignal.timeout(CASE_REQUEST_TIMEOUT_MS),
      });
      const out = readCaseOutcome(res.status, await res.json().catch(() => null));
      if (out.kind === "needEmail") {
        setFlow({ kind: "escalateEmail" });
        pushSystem(
          out.invalid
            ? "That doesn't look like a valid email — could you double-check it?"
            : "What's the best email for a specialist to reach you?",
        );
        return;
      }
      // On failure, don't trap the visitor in email mode.
      setFlow({ kind: "none" });
      // "Passed to an expert" ONLY on 2xx + a case id; otherwise honest failure + retry.
      if (out.kind === "error") {
        pushRetry(
          out.rateLimited
            ? "Your case was NOT opened — too many attempts. Please wait a few minutes, then try again."
            : "Your case was NOT opened — something went wrong on our side.",
          retry,
        );
        return;
      }
      const ref = out.ref ? ` (case ${out.ref})` : "";
      pushSystem(
        out.emailed
          ? `Done — I've opened a case${ref} and emailed you a confirmation. A specialist will follow up shortly.`
          : `Thanks — I've logged your request${ref}. A specialist will follow up at the email you gave.`,
      );
    } catch {
      setFlow({ kind: "none" });
      pushRetry("Your case was NOT opened — we couldn't reach the server.", retry);
    } finally {
      setBusy(false);
    }
  };

  const talkToSpecialist = () => {
    if (busy || flow.kind !== "none") return;
    void doEscalate();
  };

  const onQuickAction = (a: QuickAction) => {
    if (a.kind === "refund") void startRefund();
    else if (a.kind === "case") startCase(a.caseType ?? "contact_request", a.label);
    else if (a.prompt) void send(a.prompt);
  };

  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  // Show the opening quick-action menu only before the visitor has done anything
  // (the greeting is the sole message). Any action — chat, refund, or case —
  // appends a message and hides the menu.
  const showQuickActions = !busy && msgs.filter((m) => !m.notice).length <= 1 && flow.kind === "none";

  const emailStep = flow.kind === "caseEmail" || flow.kind === "escalateEmail";
  // Only the newest failure line offers "Try again" (older ones are stale).
  const lastRetryIdx = msgs.map((m) => m.ui).lastIndexOf("retry");
  const composerPlaceholder = busy
    ? "BosAI is replying…"
    : emailStep
      ? EMAIL_PLACEHOLDER
      : flow.kind === "refundReason"
        ? "Type the reason for your refund…"
        : flow.kind === "caseMessage"
          ? "Type your message…"
          : "Ask a question…";

  return (
    <>
      {/* Launcher bubble */}
      <button
        ref={launcherRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close BosAI support chat" : "Open BosAI support chat"}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="fixed bottom-6 right-6 z-[9999] inline-flex h-14 w-14 items-center justify-center rounded-full text-white transition hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        style={{ background: BOSAI_GRADIENT, boxShadow: BOSAI_GLOW }}
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {/* Chat panel */}
      {open ? (
        <div
          role="dialog"
          aria-label="BosAI support chat"
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
          className="fixed z-[9999] flex flex-col overflow-hidden border border-border bg-card text-card-foreground shadow-2xl
            inset-x-0 bottom-0 max-h-[85dvh] rounded-t-2xl
            sm:inset-x-auto sm:bottom-24 sm:right-6 sm:w-[380px] sm:max-h-[70vh] sm:rounded-2xl"
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ background: BOSAI_GRADIENT }}
            >
              <Sparkles className="h-4 w-4 text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-tight text-foreground">BosAI</div>
              <div className="truncate text-xs text-muted-foreground">File.Business support</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3 py-3"
            aria-live="polite"
            aria-atomic="false"
          >
            {msgs.map((m, i) => (
              <MessageBubble
                key={i}
                msg={m}
                onSelectPayment={chooseRefundPayment}
                disabled={busy || flow.kind !== "none"}
                onRetry={i === lastRetryIdx && retryRef.current ? onRetry : undefined}
              />
            ))}
            {showQuickActions ? (
              <div className="space-y-1.5 pt-1.5">
                {QUICK_ACTIONS.map((a) => (
                  <React.Fragment key={a.label}>
                    {a.kind === "refund" ? (
                      <div className="my-2 border-t border-border" aria-hidden="true" />
                    ) : null}
                    <button
                      type="button"
                      onClick={() => onQuickAction(a)}
                      className="group flex w-full items-center gap-2.5 rounded-xl border border-border bg-background px-3 py-2.5 text-left text-sm font-medium text-foreground transition hover:border-sky-400/60 hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                    >
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
                        style={{ background: a.kind === "sales" ? BOSAI_GRADIENT : SUPPORT_GRADIENT }}
                      >
                        <a.Icon className="h-3.5 w-3.5" />
                      </span>
                      {a.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            ) : null}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
            className="border-t border-border px-3 pb-2.5 pt-2.5"
          >
            <div className="flex items-end gap-2 rounded-xl border border-border bg-background px-2.5 py-1.5 transition focus-within:border-sky-400/70 focus-within:shadow-[0_0_0_3px_rgba(47,171,225,0.12)]">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  const el = e.target;
                  el.style.height = "auto";
                  el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
                }}
                onKeyDown={onComposerKeyDown}
                rows={1}
                disabled={busy}
                inputMode={emailStep ? "email" : undefined}
                aria-label="Message BosAI"
                placeholder={composerPlaceholder}
                className="max-h-[120px] flex-1 resize-none border-0 bg-transparent py-1 text-sm text-foreground outline-none ring-0 placeholder:text-muted-foreground focus:ring-0 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition hover:brightness-110 disabled:opacity-40"
                style={{ background: BOSAI_GRADIENT }}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>

            {/* Footer micro-copy + specialist escalation */}
            <div className="mt-2 flex items-center justify-between gap-2 px-0.5">
              <span className="text-[10px] leading-tight text-muted-foreground">
                BosAI can be wrong · Not legal advice
              </span>
              <button
                type="button"
                onClick={talkToSpecialist}
                className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-sky-600 transition hover:text-sky-500 dark:text-sky-400 focus:outline-none focus-visible:underline"
              >
                <LifeBuoy className="h-3 w-3" />
                Talk to an expert
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}

export function MessageBubble({
  msg,
  onSelectPayment,
  disabled,
  onRetry,
}: {
  msg: Msg;
  onSelectPayment: (p: Payment) => void;
  disabled: boolean;
  onRetry?: () => void;
}) {
  // Honest failure (case/escalate not created) with an optional Try again button.
  if (msg.ui === "retry") {
    return (
      <div className="flex flex-col items-center gap-1.5 py-0.5" role="alert">
        <span className="max-w-[92%] rounded-full bg-red-50 px-3 py-1 text-center text-[11px] text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {msg.content}
        </span>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-50"
            style={{ background: BOSAI_GRADIENT }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  // Login prompt: an in-chat sign-in link (the one allowed navigation — auth).
  if (msg.ui === "login") {
    return (
      <div className="flex flex-col items-center gap-1.5 py-0.5">
        <span className="max-w-[92%] rounded-full bg-muted px-3 py-1 text-center text-[11px] text-muted-foreground">
          {msg.content}
        </span>
        <a
          href="/login"
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          style={{ background: BOSAI_GRADIENT }}
        >
          <LogIn className="h-3.5 w-3.5" />
          Sign in to continue
        </a>
      </div>
    );
  }

  // Payment picker: selectable cards for the refund flow.
  if (msg.ui === "payments" && msg.payments?.length) {
    return (
      <div className="flex flex-col gap-2">
        <div className="max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-border bg-muted/60 px-3 py-2 text-sm leading-relaxed text-foreground dark:bg-slate-800/50">
          {msg.content}
        </div>
        <div className="space-y-1.5">
          {msg.payments.map((p) => {
            const meta = paymentMeta(p);
            return (
              <button
                key={p.transactionId}
                type="button"
                onClick={() => onSelectPayment(p)}
                disabled={disabled || !p.serviceId}
                className="group flex w-full items-center gap-2.5 rounded-xl border border-border bg-background px-3 py-2.5 text-left transition hover:border-sky-400/60 hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
                  style={{ background: SUPPORT_GRADIENT }}
                >
                  <CreditCard className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">{paymentTitle(p)}</span>
                  {meta ? <span className="block text-xs text-muted-foreground">{meta}</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (msg.role === "system") {
    return (
      <div className="flex justify-center py-0.5">
        <span className="rounded-full bg-muted px-3 py-1 text-[11px] text-muted-foreground">
          {msg.content}
        </span>
      </div>
    );
  }
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm px-3 py-2 text-sm text-white shadow-sm"
          style={{ background: BOSAI_GRADIENT }}
        >
          {msg.content}
        </div>
      </div>
    );
  }
  const bubble = (
    <div className="flex justify-start">
      <div className="max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-border bg-muted/60 px-3 py-2 text-sm leading-relaxed text-foreground dark:bg-slate-800/50">
        {msg.content}
        {msg.streaming ? <span className="ml-0.5 inline-block h-3.5 w-[6px] translate-y-[1px] animate-pulse rounded-sm bg-sky-400 align-middle" /> : null}
      </div>
    </div>
  );
  if (!msg.orderCard && !msg.deadlinesCard && !msg.businessCard && !msg.serviceCard) return bubble;
  return (
    <div className="flex flex-col items-start gap-2">
      {msg.orderCard ? <OrderStatusCardView card={msg.orderCard} /> : null}
      {msg.deadlinesCard ? <DeadlinesCardView card={msg.deadlinesCard} /> : null}
      {msg.businessCard ? <BusinessInfoCardView card={msg.businessCard} /> : null}
      {msg.serviceCard ? <ServiceRecommendationCardView card={msg.serviceCard} /> : null}
      {msg.content || msg.streaming ? bubble : null}
    </div>
  );
}
